import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeft, Plus, Edit3, Trash2, Star, MapPin, Truck, CheckCircle2 } from "lucide-react";
import * as ella from "@/lib/ellaApi";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AddressFormModal from "@/components/AddressFormModal";

export default function AddressesPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Opened from checkout to pick a delivery address → selection mode.
  const selectMode = params.get("from") === "checkout";
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [choosing, setChoosing] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await ella.getAddresses();
      if (res.status) setAddresses(res.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const setPrimary = async (id) => {
    const res = await ella.setPrimaryAddress(id);
    if (res.status) { toast.success(res.message || "Alamat utama diperbarui"); refresh(); }
    else toast.error(res.message || "Gagal");
  };

  // Selection mode: make this the shipping address and return to checkout.
  const chooseAddress = async (a) => {
    if (choosing) return;
    setChoosing(true);
    try {
      if (Number(a.IsDefault) !== 1) {
        const res = await ella.setPrimaryAddress(a.ID);
        if (!res.status) { toast.error(res.message || "Gagal memilih alamat"); return; }
      }
      toast.success("Alamat pengiriman dipilih");
      nav("/checkout");
    } finally { setChoosing(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus alamat ini?")) return;
    const res = await ella.removeAddress(id);
    if (res.status) { toast.success(res.message || "Alamat dihapus"); refresh(); }
    else toast.error(res.message || "Gagal menghapus");
  };

  return (
    <div className="pb-32">
      <div className="px-4 py-3 flex items-center gap-3 sticky top-[60px] bg-white z-30 border-b border-gray-100">
        <button onClick={() => nav(-1)} className="p-1 -ml-1 hover:bg-gray-100 rounded-full" data-testid="back-button">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="font-display text-lg font-bold text-gray-900">
          {selectMode ? "Pilih Alamat Pengiriman" : "Daftar Alamat"}
        </h1>
      </div>

      {selectMode && (
        <div className="mx-4 mt-3 flex items-center gap-2 bg-red-50 rounded-xl px-3 py-2 text-xs text-[#D32F2F]" data-testid="address-select-hint">
          <Truck className="w-4 h-4 shrink-0" />
          <span>Pilih alamat tujuan pengiriman, lalu kamu akan kembali ke checkout.</span>
        </div>
      )}

      <div className="p-4 space-y-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-24 skeleton rounded-2xl" />)
        ) : addresses.length === 0 ? (
          <div className="py-12 text-center">
            <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Belum ada alamat tersimpan</p>
          </div>
        ) : (
          addresses.map((a) => {
            const isDefault = Number(a.IsDefault) === 1;
            return (
            <div key={a.ID} className={`bg-white rounded-2xl border p-3 ${selectMode && isDefault ? "border-[#D32F2F]" : "border-gray-100"}`} data-testid={`address-card-${a.ID}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-sm text-gray-900">{a.Name}</p>
                    {isDefault && <span className="text-[10px] font-bold bg-red-50 text-[#D32F2F] px-1.5 py-0.5 rounded-full">{selectMode ? "TERPILIH" : "UTAMA"}</span>}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{a.Phone}</p>
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    {a.Address}, {a.DistrictName}, {a.CityName}, {a.StateName} {a.PostalCode}
                  </p>
                </div>
              </div>

              {selectMode ? (
                <>
                  <button
                    onClick={() => chooseAddress(a)}
                    disabled={choosing}
                    className="w-full mt-3 h-9 rounded-full bg-[#D32F2F] text-white text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-60"
                    data-testid={`choose-address-${a.ID}`}
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> {isDefault ? "Kirim ke Alamat Ini" : "Kirim ke Sini"}
                  </button>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => { setEditing(a); setModalOpen(true); }} className="flex-1 text-xs font-semibold text-gray-700 flex items-center justify-center gap-1 py-1.5" data-testid={`edit-address-${a.ID}`}>
                      <Edit3 className="w-3.5 h-3.5" /> Ubah
                    </button>
                    <button onClick={() => remove(a.ID)} className="flex-1 text-xs font-semibold text-red-600 flex items-center justify-center gap-1 py-1.5" data-testid={`delete-address-${a.ID}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Hapus
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                  {!isDefault && (
                    <button onClick={() => setPrimary(a.ID)} className="flex-1 text-xs font-semibold text-[#D32F2F] flex items-center justify-center gap-1 py-1.5" data-testid={`set-primary-${a.ID}`}>
                      <Star className="w-3.5 h-3.5" /> Atur Utama
                    </button>
                  )}
                  <button onClick={() => { setEditing(a); setModalOpen(true); }} className="flex-1 text-xs font-semibold text-gray-700 flex items-center justify-center gap-1 py-1.5" data-testid={`edit-address-${a.ID}`}>
                    <Edit3 className="w-3.5 h-3.5" /> Ubah
                  </button>
                  <button onClick={() => remove(a.ID)} className="flex-1 text-xs font-semibold text-red-600 flex items-center justify-center gap-1 py-1.5" data-testid={`delete-address-${a.ID}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Hapus
                  </button>
                </div>
              )}
            </div>
            );
          })
        )}
      </div>

      <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-gray-100 px-4 py-3 z-40 space-y-2">
        {selectMode && (
          <Button variant="outline" onClick={() => nav("/checkout")} className="w-full h-11 rounded-full" data-testid="address-back-to-checkout">
            Kembali ke Checkout
          </Button>
        )}
        <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="w-full h-11 btn-primary" data-testid="add-address-button">
          <Plus className="w-4 h-4 mr-1" /> Tambah Alamat Baru
        </Button>
      </div>

      <AddressFormModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={refresh} initial={editing} />
    </div>
  );
}
